import React, { ComponentProps, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import BaseChannelSidebar from 'src/components/Channels/BaseChannelSidebar';
import { AvatarInfoType } from 'src/components/Channels/ChannelSidebarItem';
import { InboxSidebarItem } from 'src/components/Inbox/InboxSidebarItem';
import { InboxPageContext } from 'src/context/inboxPageContext';
import {
  InboxNotificationDetailsResponse,
  NotificationEventType,
} from 'src/entities/Notifications';
import {
  DeleteAllNotificationsType,
  useDeleteAllNotificationsMutation,
  useDeleteNotificationMutation,
  useGetInboxNotificationsQuery,
  useUpdateAllNotificationsMutation,
  useUpdateNotificationMutation,
} from 'src/services/api/inboxApi';
import { RootState } from 'src/store';
import { NotificationGroups } from 'src/store/notifications/types';
import Button from '../Button';
import { useChannelNavigationShortcut } from '../Channels/useChannelNavigation';
import { Action, BaseMenu } from '../Dropdowns';
import {
  ListIcon,
  TrashIcon,
  TrayFullOutline as MarkUnreadIcon,
  TrayOutlineIcon as MarkReadIcon,
} from '../Icons';

export enum InboxNotificationStatus {
  Read = 'read',
  Sent = 'sent',
}

/**
 * This component is used to show the inbox sidebar actions:
 * Mark all as read, Mark all as unread, Delete all notifications
 */
const InboxNotificationsViewActions: React.FC<{
  totalUnreadNotificationsCount: number;
  totalReadNotificationsCount: number;
}> = ({ totalUnreadNotificationsCount, totalReadNotificationsCount }) => {
  const { selectedNotification, setSelectedNotification } =
    useContext(InboxPageContext);
  const [viewButtonAnchor, setViewButtonAnchor] =
    React.useState<HTMLElement | null>(null);
  const [updateNotification] = useUpdateNotificationMutation();
  const [updateAllNotifications] = useUpdateAllNotificationsMutation();
  const [deleteNotification] = useDeleteNotificationMutation();
  const [deleteAllNotifications] = useDeleteAllNotificationsMutation();
  const handleDeleteNotification = () => {
    if (selectedNotification) {
      deleteNotification({
        id: selectedNotification?.id || '',
      });
      setSelectedNotification(null);
    }
  };
  const handleDeleteAllNotifications = (type: DeleteAllNotificationsType) => {
    deleteAllNotifications({ type });
    setSelectedNotification(null);
  };
  // handles updating the selected notification read status
  const handleUpdateSelectedNotificationStatus = (isRead: boolean) => {
    if (selectedNotification) {
      updateNotification({
        id: selectedNotification?.id || '',
        isRead,
      });
      setSelectedNotification({ ...selectedNotification, isRead });
    }
  };
  const handleUpdateAllNotifications = (isRead: boolean) => {
    updateAllNotifications({ isRead });
    if (selectedNotification) {
      setSelectedNotification({ ...selectedNotification, isRead });
    }
  };

  /**
   * This function setup the view button actions based on the scenarios
   * described in the figma design.
   * @link https://www.figma.com/file/QZOvjHYQLyIWWMuPXYqTc2/Main?type=design&node-id=40180-38418&mode=design&t=MDjRRazd6ONBRAJQ-0
   */
  const getViewDropdownActions = useCallback(() => {
    let actions: Array<Action> = [];
    // if no notification is selected and there are no notifications
    // actions menu should only show 'Delete all notifications'
    if (!selectedNotification) {
      if (totalUnreadNotificationsCount === 0) {
        actions = [
          {
            name: 'Delete all notifications',
            onClick: () =>
              handleDeleteAllNotifications(DeleteAllNotificationsType.ALL),
            isDelete: true,
            icon: <TrashIcon style={{ fontSize: 12 }} />,
          },
        ];

        return actions;
      }

      actions = [
        {
          name: 'Mark all notifications read',
          onClick: () => handleUpdateAllNotifications(true),
          icon: <MarkReadIcon style={{ fontSize: 12 }} />,
          hasDivider: true,
        },
        // if there are read notifications then surface 'Delete all read notifications'
        ...(totalReadNotificationsCount > 0
          ? [
              {
                name: 'Delete all read notifications',
                onClick: () =>
                  handleDeleteAllNotifications(DeleteAllNotificationsType.READ),
                isDelete: true,
                icon: <TrashIcon style={{ fontSize: 12 }} />,
              },
            ]
          : []),
        {
          name: 'Delete all notifications',
          onClick: () =>
            handleDeleteAllNotifications(DeleteAllNotificationsType.ALL),
          isDelete: true,
          icon: <TrashIcon style={{ fontSize: 12 }} />,
        },
      ];

      return actions;
    }

    // when selected notification is read and there is no unread notifications
    if (selectedNotification.isRead && totalUnreadNotificationsCount === 0) {
      actions = [
        {
          name: 'Mark unread',
          onClick: () => handleUpdateSelectedNotificationStatus(false),
          icon: <MarkUnreadIcon style={{ fontSize: 12 }} />,
          hasDivider: true,
        },
        {
          name: 'Delete notification',
          onClick: handleDeleteNotification,
          isDelete: true,
          icon: <TrashIcon style={{ fontSize: 12 }} />,
        },
        {
          name: 'Delete all notifications',
          onClick: () =>
            handleDeleteAllNotifications(DeleteAllNotificationsType.ALL),
          isDelete: true,
          icon: <TrashIcon style={{ fontSize: 12 }} />,
        },
      ];
      return actions;
    }

    // if selected notification is unread and all notifications are unread
    if (
      !selectedNotification.isRead &&
      totalUnreadNotificationsCount > 0 &&
      totalReadNotificationsCount === 0
    ) {
      actions = [
        {
          name: 'Mark read',
          onClick: () => handleUpdateSelectedNotificationStatus(true),
          icon: <MarkReadIcon style={{ fontSize: 12 }} />,
          hasDivider: true,
        },
        {
          name: 'Mark all notifications read',
          onClick: () => handleUpdateAllNotifications(true),
          icon: <MarkReadIcon style={{ fontSize: 12 }} />,
          hasDivider: true,
        },
        {
          name: 'Delete notification',
          onClick: handleDeleteNotification,
          isDelete: true,
          icon: <TrashIcon style={{ fontSize: 12 }} />,
        },
        {
          name: 'Delete all notifications',
          onClick: () =>
            handleDeleteAllNotifications(DeleteAllNotificationsType.ALL),
          isDelete: true,
          icon: <TrashIcon style={{ fontSize: 12 }} />,
        },
      ];
    }

    // when there are both unread and read notifications
    if (totalUnreadNotificationsCount > 0 && totalReadNotificationsCount > 0) {
      actions = selectedNotification.isRead
        ? [
            {
              name: 'Mark unread',
              onClick: () => handleUpdateSelectedNotificationStatus(false),
              icon: <MarkUnreadIcon style={{ fontSize: 12 }} />,
              hasDivider: true,
            },
            {
              name: 'Mark all notifications read',
              onClick: () => handleUpdateAllNotifications(true),
              icon: <MarkReadIcon style={{ fontSize: 12 }} />,
              hasDivider: true,
            },
            {
              name: 'Delete notification',
              onClick: handleDeleteNotification,
              isDelete: true,
              icon: <TrashIcon style={{ fontSize: 12 }} />,
            },
            {
              name: 'Delete all read notifications',
              onClick: () =>
                handleDeleteAllNotifications(DeleteAllNotificationsType.READ),
              isDelete: true,
              icon: <TrashIcon style={{ fontSize: 12 }} />,
            },
            {
              name: 'Delete all notifications',
              onClick: () =>
                handleDeleteAllNotifications(DeleteAllNotificationsType.ALL),
              isDelete: true,
              icon: <TrashIcon style={{ fontSize: 12 }} />,
            },
          ]
        : [
            {
              name: 'Mark read',
              onClick: () => handleUpdateSelectedNotificationStatus(true),
              icon: <MarkReadIcon style={{ fontSize: 12 }} />,
              hasDivider: true,
            },
            {
              name: 'Mark all notifications read',
              onClick: () => handleUpdateAllNotifications(true),
              icon: <MarkReadIcon style={{ fontSize: 12 }} />,
              hasDivider: true,
            },
            {
              name: 'Delete notification',
              onClick: handleDeleteNotification,
              isDelete: true,
              icon: <TrashIcon style={{ fontSize: 12 }} />,
            },
            {
              name: 'Delete all read notifications',
              onClick: () =>
                handleDeleteAllNotifications(DeleteAllNotificationsType.READ),
              isDelete: true,
              icon: <TrashIcon style={{ fontSize: 12 }} />,
            },
            {
              name: 'Delete all notifications',
              onClick: () =>
                handleDeleteAllNotifications(DeleteAllNotificationsType.ALL),
              isDelete: true,
              icon: <TrashIcon style={{ fontSize: 12 }} />,
            },
          ];
    }
    return actions;
  }, [
    totalUnreadNotificationsCount,
    totalReadNotificationsCount,
    selectedNotification,
  ]);

  return (
    <>
      <Button
        onClick={(e) => {
          setViewButtonAnchor(e.currentTarget);
        }}
        variant="contained"
        color="secondary"
        htmlId="view-notifications-actions-button"
        startIcon={<ListIcon style={{ fontSize: 12 }} />}
      >
        Actions
      </Button>
      <BaseMenu
        key="view-notifications-actions-menu"
        menuProps={{
          open: Boolean(viewButtonAnchor),
          anchorEl: viewButtonAnchor,
          onClose: () => {
            setViewButtonAnchor(null);
          },
          getContentAnchorEl: null,
          keepMounted: true,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
        }}
        actions={getViewDropdownActions()}
      />
    </>
  );
};

export interface InboxSidebarItemData {
  id?: string;
  title: string;
  label: string; // TODO(EnableInboxSidebarItemRedesign): legacy prop
  description: string;
  notificationStatus: InboxNotificationStatus;
  avatarInfo: (AvatarInfoType & { email: string; companyName?: string }) | null;
  notificationGroup: NotificationGroups | string;
  channelId: string;
  data: string;
  isRead: boolean;
  timestamp: Date;
  resourceId: string;
  refId: string;
  notificationEventType: NotificationEventType;
}

export const InboxSidebar = () => {
  const { data: inboxNotifications } = useGetInboxNotificationsQuery();

  // get total read and unread notifications count
  const { unreadNotificationCount, readNotificationCount } =
    React.useMemo(() => {
      const allNotifications: Array<InboxNotificationDetailsResponse> =
        inboxNotifications || [];
      const totalUnreadNotifications = allNotifications?.reduce(
        (prev, curr) => {
          if (!curr.isRead) return prev + 1;
          return prev;
        },
        0,
      );
      return {
        unreadNotificationCount: totalUnreadNotifications,
        readNotificationCount:
          allNotifications.length - totalUnreadNotifications,
      };
    }, [inboxNotifications]);

  const searchKey = useSelector((state: RootState) => state.ui.searchValue);
  const { setSelectedNotification, selectedNotification } =
    React.useContext(InboxPageContext);
  const clients = useSelector((state: RootState) => state.clients.clients);
  const internalUsers = useSelector(
    (state: RootState) => state.users.internalUsers,
  );
  const companyDataMap = useSelector(
    (state: RootState) => state.clients.companyDataMap,
  );

  // all users ( internal + client )
  const allUsers = [...clients, ...internalUsers];
  // on click we want to set secondary sidebar info as notification id
  const onSelectChannel: ComponentProps<
    typeof BaseChannelSidebar
  >['onSelectChannel'] = (sidebarItemOption: InboxSidebarItemData) => {
    setSelectedNotification({
      ...sidebarItemOption,
      isRead: true,
    });
  };

  React.useEffect(
    () => () => {
      // reset inbox sidebar
      setSelectedNotification(null);
    },
    [],
  );

  const formattedNotificationsList = React.useMemo(() => {
    if (!inboxNotifications) return [];

    const notifications: InboxSidebarItemData[] = inboxNotifications
      .slice() // creating copy of array
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ) // latest notification first
      .map((notification) => {
        const { fields } = notification;

        const notificationGroup = (fields?.groupingEntityType ||
          fields?.notificationGroup ||
          '') as NotificationGroups;

        const sender = allUsers.find((c) => c.id === fields.senderUserId);
        // notification sender name
        // incase user is deleted show sender name as 'Deleted User'
        const senderName = sender
          ? `${sender.fields.givenName} ${sender.fields.familyName}`
          : 'Deleted User';

        let description = '';
        let senderCompanyName;

        // generate notification description
        // based on the notification group
        switch (notificationGroup) {
          case NotificationGroups.FileUploadNotification: {
            const companyId = sender?.fields.companyId;
            senderCompanyName = companyId
              ? companyDataMap[companyId]?.name
              : '';
            // if sender belongs to a company add company name in notification description
            if (senderCompanyName) {
              description = `${senderName} from ${senderCompanyName}`;
            } else {
              description = `${senderName}`;
            }
            // 'file' for count = 1, 'files' for count > 1
            description = `${description} uploaded ${fields.count} file${
              fields.count && fields.count > 1 ? 's' : ''
            }`;
            break;
          }

          case NotificationGroups.EsigRequestCompleteNotifications: {
            description = `${
              fields?.itemName || 'File'
            } has been signed by all parties`;
            break;
          }

          default:
            description = '';
        }

        // for forms complete use the notification event prop
        if (notification.event === 'forms.completed') {
          description = `A form was submitted by ${senderName}`;
        }

        const isCompanyUser = !!senderCompanyName;
        const {
          email,
          fallbackColor: senderPictureFallbackColor,
          picture: senderPicture,
        } = sender?.fields || {};

        const avatarInfo = {
          email,
          isCompanyUser,
          name: `${senderName}${
            senderCompanyName ? ` from ${senderCompanyName}` : ''
          }`,
          companyName:
            companyDataMap[sender?.fields.companyId || '']?.name || '',
          imageUrl: isCompanyUser
            ? companyDataMap[sender?.fields.companyId || '']?.avatarUrl || ''
            : senderPicture || '',

          fallbackColor: isCompanyUser
            ? companyDataMap[sender?.fields.companyId || '']
                ?.avatarFallbackColor
            : senderPictureFallbackColor,
        };

        return {
          id: notification.id,
          title: notification.title,
          isRead: notification.isRead,
          description,
          avatarInfo,
          notificationGroup,
          channelId: fields.channelId,
          data: notification.data,
          timestamp: new Date(notification.createdAt),
          resourceId: notification.resourceId,
          refId: notification.ref,
          notificationEventType: notification.event,
        };
      }) as InboxSidebarItemData[];

    return notifications;
  }, [inboxNotifications, allUsers]);

  const filteredNotificationList = React.useMemo(() => {
    const notificationList = [...formattedNotificationsList];
    // if search value is empty then return all the notifications available in the list
    if (!searchKey) return notificationList;

    const queriedNotifications = notificationList.filter((n) =>
      n.title.toLowerCase().includes(searchKey.toLowerCase()),
    );

    // if the selected notification is not included in the queried result
    // de select the notification
    if (selectedNotification) {
      const selectedNotificationExist = queriedNotifications.find(
        (n) => n.id === selectedNotification.id,
      );
      if (!selectedNotificationExist) {
        setSelectedNotification(null);
      }
    }

    // return only those notifications that meet our search criteria
    return queriedNotifications;
  }, [searchKey, formattedNotificationsList, selectedNotification]);

  // handle channel notification shortcuts for inbox list
  useChannelNavigationShortcut(
    filteredNotificationList,
    filteredNotificationList.findIndex(
      (notification) => notification.id === selectedNotification?.id,
    ),
    onSelectChannel,
  );

  if (formattedNotificationsList.length < 1) return null;

  return (
    <BaseChannelSidebar
      title="Files Notifications"
      entity="inbox"
      sidebarItemOptions={filteredNotificationList}
      onSelectChannel={onSelectChannel}
      itemRenderer={InboxSidebarItem}
      noCreateAction
      headerActionsSlot={
        <InboxNotificationsViewActions
          totalUnreadNotificationsCount={unreadNotificationCount || 0}
          totalReadNotificationsCount={readNotificationCount || 0}
        />
      }
      shouldSetQueryParam={false}
      disableDefaultSelect
    />
  );
};
